import { NotificationIcon } from "@config/local-notifications/notification-icon";
import { WEME_SUPPORT_WHATSAPP_URL } from "@shared/helpers/constants";

export enum NotificationTemplateKey {
  // Request Status Lifecycle
  REQUEST_RECEIVED = 'REQUEST_RECEIVED',
  REQUEST_REJECTED = 'REQUEST_REJECTED',
  REQUEST_APPROVED = 'REQUEST_APPROVED',
  MATCH_FOUND = 'MATCH_FOUND',
  SEEKER_VOLUNTEER_REGISTERED = 'SEEKER_VOLUNTEER_REGISTERED',
  SEEKER_VOLUNTEER_REGISTERED_REGULAR = 'SEEKER_VOLUNTEER_REGISTERED_REGULAR',
  VOLUNTEER_REGISTERED = 'VOLUNTEER_REGISTERED',
  VOLUNTEER_REGISTERED_CHAT = 'VOLUNTEER_REGISTERED_CHAT',
  VOLUNTEER_REGISTERED_WITH_SEEKER_DETAILS = 'VOLUNTEER_REGISTERED_WITH_SEEKER_DETAILS',
  VOLUNTEER_REGISTRATION_PENDING = 'VOLUNTEER_REGISTRATION_PENDING',

  // Chat System
  NEW_CHAT_MESSAGE = 'NEW_CHAT_MESSAGE',
  NEW_CHAT_MESSAGE_VOLUNTEER = 'NEW_CHAT_MESSAGE_VOLUNTEER',
  UNREAD_MESSAGE_REMINDER = 'UNREAD_MESSAGE_REMINDER',
  REVEAL_SEEKER_DETAILS_TO_VOLUNTEER = 'REVEAL_SEEKER_DETAILS_TO_VOLUNTEER',

  // Cancellations & Rejections
  VOLUNTEER_CANCELLED = 'VOLUNTEER_CANCELLED',
  SEEKER_CANCELLED_REQUEST = 'SEEKER_CANCELLED_REQUEST',
  SEEKER_DECLINED_VOLUNTEER = 'SEEKER_DECLINED_VOLUNTEER',

  // Timeline Events
  EVENT_REMINDER_24H = 'EVENT_REMINDER_24H',
  SEEKER_EVENT_REMINDER_24H = 'SEEKER_EVENT_REMINDER_24H',
  POST_EVENT_FEEDBACK = 'POST_EVENT_FEEDBACK',
  SEEKER_POST_EVENT_FEEDBACK = 'SEEKER_POST_EVENT_FEEDBACK',

  // Admin: SMS to relevant volunteers for an opportunity
  RELEVANT_VOLUNTEERS_SMS = 'RELEVANT_VOLUNTEERS_SMS',
}

export const DEFAULT_NOTIFICATION_TEMPLATES: Record<
  NotificationTemplateKey,
  string
> = {
  [NotificationTemplateKey.REQUEST_RECEIVED]:
    'היי {{user_name}}, קיבלנו את הבקשה שלך לגבי "{{request_title}}" ❤️\nאנחנו עוברים עליה ממש עכשיו, ונעדכן אותך ב-48 השעות הקרובות לכל המאוחר.\nאפשר לעקוב אחרי הסטטוס שלה ממש כאן: {{link}}',

  [NotificationTemplateKey.REQUEST_REJECTED]:
    'היי {{user_name}}, תודה שפנית אלינו 🙏\nלצערנו לא נוכל לאשר את הבקשה כפי שהיא כרגע (ייתכן שחסרים פרטים או שהיא לא תואמת את סוגי הסיוע שלנו).\nנשמח לעזור לך לדייק אותה כדי שנוכל לאשר! כתבו לנו בווצאפ לכל שאלה והתייעצות: {{whatsapp_link}}',

  [NotificationTemplateKey.REQUEST_APPROVED]:
    'היי {{user_name}}, הבקשה שלך אושרה! 🌟\nהיא מופיעה כעת בלוח שלנו ופתוחה לקהילת המתנדבים.\nברגע שמישהו יירשם לעזור, נשלח לך עדכון מיד.\nלעדכון או שינוי של הבקשה: {{link}}',

  [NotificationTemplateKey.MATCH_FOUND]:
    'היי {{user_name}}, {{who_registered}}.\n{{task_lines}}\nיש להיכנס לצ\'אט ולשלוח הודעה לתיאום.\nטלפון: {{volunteer_phone}}\nלמעבר לצ\'אט: {{link_to_chat}}',

  [NotificationTemplateKey.SEEKER_VOLUNTEER_REGISTERED]:
    'היי {{user_name}}, {{who_registered}}.\n{{task_lines}}\nצרו קשר עם המתנדב/ת לתיאום.\nשם: {{volunteer_name}}\nטלפון: {{volunteer_phone}}\nלפרטים נוספים: {{link}}',

  [NotificationTemplateKey.SEEKER_VOLUNTEER_REGISTERED_REGULAR]:
    'היי {{user_name}}, {{who_registered}}.\n{{task_lines}}\nטלפון: {{volunteer_phone}}\nלמעקב ופרטים: {{link}}',

  [NotificationTemplateKey.VOLUNTEER_REGISTERED]:
    'היי {{user_name}}, {{next_step}}\n{{schedule_lines}}\nההתנדבות: "{{request_title}}"{{date_suffix}}.\nלכל הפרטים: {{link}}',

  [NotificationTemplateKey.VOLUNTEER_REGISTERED_CHAT]:
    'היי {{user_name}}, {{next_step}}\n{{schedule_lines}}\nההתנדבות: "{{request_title}}"{{date_suffix}}.\nלמעבר לצ\'אט: {{link_to_chat}}',

  [NotificationTemplateKey.VOLUNTEER_REGISTERED_WITH_SEEKER_DETAILS]:
    'היי {{user_name}}, {{next_step}}\n{{schedule_lines}}\n{{seeker_details_block}}\nההתנדבות: "{{request_title}}"{{date_suffix}}.\nלפרטים: {{link}}',

  [NotificationTemplateKey.VOLUNTEER_REGISTRATION_PENDING]:
    'היי {{user_name}}, {{next_step}}\n{{schedule_lines}}\nההתנדבות: "{{request_title}}"{{date_suffix}}.\nלמעקב: {{link}}',

  [NotificationTemplateKey.NEW_CHAT_MESSAGE]:
    'היי {{user_name}}, מחכה לך הודעה מ-{{sender_name}} שטרם נקראה. ✉️\nמשימה מקושרת: {{request_type}}.\nלצפייה במסר ולמענה - כאן: {{link_to_chat}}',

  [NotificationTemplateKey.NEW_CHAT_MESSAGE_VOLUNTEER]:
    'היי {{user_name}}, קיבלת הודעה חדשה מ-{{sender_name}}! ✉️\nלגבי הסיוע ב"{{request_type}}". לחצ/י כאן כדי לקרוא ולענות: {{link_to_chat}}',

  [NotificationTemplateKey.UNREAD_MESSAGE_REMINDER]:
    'היי {{user_name}}, רק תזכורת קטנה - מחכה לך הודעה מ-{{sender_name}} שלא נקראה ✉️\nכדי שהסיוע יצא לפועל בצורה חלקה, כדאי להיכנס ולענות: {{link_to_chat}}',

  [NotificationTemplateKey.REVEAL_SEEKER_DETAILS_TO_VOLUNTEER]:
    '{{details_block}}',

  [NotificationTemplateKey.VOLUNTEER_CANCELLED]:
    'היי {{user_name}}, לצערנו {{volunteer_name}} לא יוכל/תוכל להגיע בסוף{{occurrence_suffix}}.\nחשוב לנו שתדע/י שלא שכחנו אותך - הבקשה חזרה מיד ללוח שלנו, ואנחנו עושים הכל כדי למצוא מתנדב/ת חלופי/ת בהקדם.\nלעדכון או שינוי הצורך: {{link}}',

  [NotificationTemplateKey.SEEKER_CANCELLED_REQUEST]:
    'היי {{user_name}}, מבקש/ת הסיוע עדכנ/ה שהעזרה כבר לא נדרשת, ולכן ההרשמה שלך בוטלה.\nתודה ענקית על הרצון הטוב! ❤️ נשמח לראות אותך בהזדמנויות התנדבות אחרות שמחכות בלוח: {{feed_link}}',

  [NotificationTemplateKey.SEEKER_DECLINED_VOLUNTEER]:
    'היי {{user_name}}, תודה ענקית על ההתגייסות! ❤️\nהפעם, לצערנו, ההתנדבות הזו לא יצאה לפועל. אנחנו מעריכים מאוד את הרצון הטוב שלך!\nיש עוד המון אנשים שישמחו לעזרה שלך בלוח: {{feed_link}}',

  [NotificationTemplateKey.EVENT_REMINDER_24H]:
    'היי {{user_name}}, מחר זה קורה! 🙌\nרק מזכירים שקבעת לעזור ב"{{request_title}}"{{date_suffix}}.\nתודה ענקית שאת/ה שם בשבילם. שאלה? כתבו לנו בווצאפ: {{whatsapp_link}}\nצריכ/ה לעדכן או לבטל? {{cancel_link}}',

  [NotificationTemplateKey.SEEKER_EVENT_REMINDER_24H]:
    'היי {{user_name}}, רק מזכירים שמחר זה קורה! 🙌\n{{volunteers_summary}} לעזור עם "{{request_title}}"{{date_suffix}}.\nשאלה? כתבו לנו בווצאפ: {{whatsapp_link}}\nלעדכון או ביטול: {{link}}',

  [NotificationTemplateKey.POST_EVENT_FEEDBACK]:
    'היי {{user_name}}, איך היה בהתנדבות? 😊\nאנחנו מקווים שהייתה לך חוויה משמעותית ב"{{request_title}}".\nהמשוב שלך ממש עוזר לנו להשתפר ולהיות מדויקים יותר בפעמים הבאות. נשמח ל-60 שניות מזמנך: {{feedback_link}}',

  [NotificationTemplateKey.SEEKER_POST_EVENT_FEEDBACK]:
    'היי {{user_name}}, אנחנו מקווים שהכל עבר בצורה חלקה ושקיבלת את העזרה שהיית צריך/ה עם "{{request_title}}" ❤️\nהמילים שלך מאוד עוזרות לנו להשתפר - נשמח לשמוע איך היה במשוב קצר ומהיר: {{feedback_link}}',

  [NotificationTemplateKey.RELEVANT_VOLUNTEERS_SMS]:
    'היי {{user_name}}, יש משימה שעשויה להתאים לך: {{request_type}}.\nתאריך: {{date}}\nמיקום: {{location}}\nלפרטים והרשמה: {{feed_link}}',
};

/** Short push title/body per template (SMS stays full in DEFAULT_NOTIFICATION_TEMPLATES). */
export const NOTIFICATION_PUSH_TEMPLATES: Record<
  NotificationTemplateKey,
  { title: string; body: string }
> = {
  [NotificationTemplateKey.REQUEST_RECEIVED]: {
    title: "קיבלנו את הבקשה ❤️",
    body: '"{{request_title}}" - נעדכן תוך 48 שעות',
  },
  [NotificationTemplateKey.REQUEST_APPROVED]: {
    title: "הבקשה שלך אושרה! 🌟",
    body: "היא מופיעה כעת בלוח שלנו. ברגע שמישהו יירשם לעזור - נעדכן מיד.",
  },
  [NotificationTemplateKey.REQUEST_REJECTED]: {
    title: "עדכון לגבי הבקשה",
    body: "לא ניתן לאשר כרגע - אנחנו כאן לעזור לדייק.",
  },
  [NotificationTemplateKey.MATCH_FOUND]: {
    title: "נרשם מישהו לעזור",
    body: '{{who_registered}} ל"{{request_title}}"{{task_push}}. שלחו הודעה בצ\'אט. הטלפון בהודעה.',
  },
  [NotificationTemplateKey.SEEKER_VOLUNTEER_REGISTERED]: {
    title: "נרשם מישהו לעזור",
    body: '{{who_registered}} ל"{{request_title}}"{{task_push}}. צרו קשר. הפרטים בהודעה.',
  },
  [NotificationTemplateKey.SEEKER_VOLUNTEER_REGISTERED_REGULAR]: {
    title: "נרשם מישהו לעזור",
    body: '{{who_registered}} ל"{{request_title}}"{{task_push}}. מספר הטלפון בהודעה.',
  },
  [NotificationTemplateKey.VOLUNTEER_REGISTERED]: {
    title: "{{notice_title}}",
    body: "{{next_step}}\n{{schedule_lines}}",
  },
  [NotificationTemplateKey.VOLUNTEER_REGISTERED_CHAT]: {
    title: "{{notice_title}}",
    body: "{{next_step}}\n{{schedule_lines}}",
  },
  [NotificationTemplateKey.VOLUNTEER_REGISTERED_WITH_SEEKER_DETAILS]: {
    title: "{{notice_title}}",
    body: "{{next_step}}\n{{schedule_lines}}",
  },
  [NotificationTemplateKey.VOLUNTEER_REGISTRATION_PENDING]: {
    title: "{{notice_title}}",
    body: "{{next_step}}\n{{schedule_lines}}",
  },
  [NotificationTemplateKey.VOLUNTEER_CANCELLED]: {
    title: "מחפשים מתנדב/ת חלופי/ת לבקשה",
    body: "{{volunteer_name}} נאלצ/ה לבטל{{occurrence_suffix}}. החזרנו את הבקשה ללוח כדי למצוא מישהו אחר.",
  },
  [NotificationTemplateKey.NEW_CHAT_MESSAGE]: {
    title: "הודעה חדשה",
    body: "מ-{{sender_name}} - לחצ/י לצפייה ומענה.",
  },
  [NotificationTemplateKey.NEW_CHAT_MESSAGE_VOLUNTEER]: {
    title: "הודעה חדשה מ-{{sender_name}} ✉️",
    body: "לחצ/י כאן כדי לקרוא ולענות במהירות.",
  },
  [NotificationTemplateKey.UNREAD_MESSAGE_REMINDER]: {
    title: "תזכורת: הודעה שלא נקראה ✉️",
    body: "מחכה לך הודעה מ-{{sender_name}}. כדאי להיכנס כדי להמשיך בתיאום.",
  },
  [NotificationTemplateKey.REVEAL_SEEKER_DETAILS_TO_VOLUNTEER]: {
    title: "פרטי הקשר נחשפו 📞",
    body: "ניתן כעת לצפות בפרטים ליצירת קשר בתוך הצ'אט שלכם.",
  },
  [NotificationTemplateKey.EVENT_REMINDER_24H]: {
    title: "מחר זה קורה! 🙌",
    body: 'ההתנדבות ב-"{{request_title}}"{{date_suffix}}. לחצ/י לכל הפרטים וההיערכות.',
  },
  [NotificationTemplateKey.SEEKER_EVENT_REMINDER_24H]: {
    title: "מחר זה קורה! ❤️",
    body: "{{volunteers_summary}} לעזור עם הבקשה שלך. לחצ/י כדי לראות את כל הפרטים לקראת מחר.",
  },
  [NotificationTemplateKey.POST_EVENT_FEEDBACK]: {
    title: "איך היה בהתנדבות? 😊",
    body: "נשמח ל-60 שניות מזמנך כדי לשמוע איך הייתה ההתנדבות שלך.",
  },
  [NotificationTemplateKey.SEEKER_POST_EVENT_FEEDBACK]: {
    title: "איך היה? ❤️",
    body: "נשמח לשמוע בלחיצה כאן אם קיבלת את העזרה שחיפשת.",
  },
  [NotificationTemplateKey.SEEKER_CANCELLED_REQUEST]: {
    title: "הבקשה לסיוע בוטלה",
    body: "מבקש/ת הסיוע הסתדר/ה, אז ההרשמה בוטלה. תודה על הרצון הטוב! ❤️",
  },
  [NotificationTemplateKey.SEEKER_DECLINED_VOLUNTEER]: {
    title: "תודה על הנכונות! ❤️",
    body: "הפעם ההתנדבות לא יצאה לפועל, אבל יש עוד המון התנדבויות שמחכות לך בלוח.",
  },
  [NotificationTemplateKey.RELEVANT_VOLUNTEERS_SMS]: {
    title: "מצאנו התנדבות שבול בשבילך! 🎯",
    body: "{{request_type}} - לחצ/י כדי לראות את כל הפרטים ולהירשם.",
  },
};

export const NOTIFICATION_TEMPLATE_ICONS: Partial<
  Record<NotificationTemplateKey, NotificationIcon>
> = {
  [NotificationTemplateKey.REQUEST_RECEIVED]: NotificationIcon.CLOCK,
  [NotificationTemplateKey.REQUEST_APPROVED]: NotificationIcon.NEW,
  [NotificationTemplateKey.REQUEST_REJECTED]: NotificationIcon.CANCEL,
  [NotificationTemplateKey.MATCH_FOUND]: NotificationIcon.USER_ADD,
  [NotificationTemplateKey.SEEKER_VOLUNTEER_REGISTERED]: NotificationIcon.USER_ADD,
  [NotificationTemplateKey.SEEKER_VOLUNTEER_REGISTERED_REGULAR]: NotificationIcon.USER_ADD,
  [NotificationTemplateKey.VOLUNTEER_REGISTERED]: NotificationIcon.USER_ADD,
  [NotificationTemplateKey.VOLUNTEER_REGISTERED_CHAT]: NotificationIcon.USER_ADD,
  [NotificationTemplateKey.VOLUNTEER_REGISTERED_WITH_SEEKER_DETAILS]: NotificationIcon.USER_ADD,
  [NotificationTemplateKey.VOLUNTEER_REGISTRATION_PENDING]: NotificationIcon.CLOCK,
  [NotificationTemplateKey.VOLUNTEER_CANCELLED]: NotificationIcon.USER_CANCELED,
  [NotificationTemplateKey.SEEKER_CANCELLED_REQUEST]: NotificationIcon.EVENT_CANCELED,
  [NotificationTemplateKey.SEEKER_DECLINED_VOLUNTEER]: NotificationIcon.USER_CANCELED,
  [NotificationTemplateKey.NEW_CHAT_MESSAGE]: NotificationIcon.INFO,
  [NotificationTemplateKey.NEW_CHAT_MESSAGE_VOLUNTEER]: NotificationIcon.INFO,
  [NotificationTemplateKey.UNREAD_MESSAGE_REMINDER]: NotificationIcon.CLOCK,
  [NotificationTemplateKey.REVEAL_SEEKER_DETAILS_TO_VOLUNTEER]: NotificationIcon.INFO,
  [NotificationTemplateKey.EVENT_REMINDER_24H]: NotificationIcon.CLOCK,
  [NotificationTemplateKey.SEEKER_EVENT_REMINDER_24H]: NotificationIcon.CLOCK,
  [NotificationTemplateKey.POST_EVENT_FEEDBACK]: NotificationIcon.NEW,
  [NotificationTemplateKey.SEEKER_POST_EVENT_FEEDBACK]: NotificationIcon.NEW,
  [NotificationTemplateKey.RELEVANT_VOLUNTEERS_SMS]: NotificationIcon.NEW,
};

export type NotificationChannelContent = {
  smsMessage: string;
  pushTitle: string;
  pushBody: string;
  inAppTitle: string;
  inAppContent: string;
  icon: NotificationIcon;
};

/**
 * Replaces placeholders in a template string with actual values
 */
export function replaceTemplatePlaceholders(
  template: string,
  placeholders: Record<string, string>,
): string {
  let result = template;
  for (const [key, value] of Object.entries(placeholders)) {
    result = result.replace(
      new RegExp(`{{${key}}}`, 'g'),
      value || '',
    );
  }
  return result;
}

/** Returns true when text still contains an http(s) URL. */
export function containsUrlInNotificationText(text: string): boolean {
  return /https?:\/\/[^\s\n]+/i.test(text);
}

/** Removes URLs and cleans trailing punctuation left after placeholder removal. */
export function stripUrlsFromNotificationText(text: string): string {
  return text
    .replace(/https?:\/\/[^\s\n]+/gi, "")
    .replace(/[ \t]*:\s*(?=\n|$)/gm, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Final in-app body: short push text only, never SMS links. */
export function sanitizeInAppNotificationContent(text: string): string {
  return stripUrlsFromNotificationText(text);
}

/** Builds SMS (full), push (short), and in-app content from a template key. */
export function buildNotificationChannelContent(
  templateKey: NotificationTemplateKey,
  placeholders: Record<string, string>,
): NotificationChannelContent {
  const placeholdersWithSupport = {
    whatsapp_link: WEME_SUPPORT_WHATSAPP_URL,
    ...placeholders,
  };
  const smsMessage = replaceTemplatePlaceholders(
    DEFAULT_NOTIFICATION_TEMPLATES[templateKey],
    placeholdersWithSupport,
  );
  const pushMeta = NOTIFICATION_PUSH_TEMPLATES[templateKey];
  const pushTitle = replaceTemplatePlaceholders(
    pushMeta.title,
    placeholdersWithSupport,
  );
  const pushBody = replaceTemplatePlaceholders(
    pushMeta.body,
    placeholdersWithSupport,
  );
  const icon =
    NOTIFICATION_TEMPLATE_ICONS[templateKey] ?? NotificationIcon.INFO;
  const inAppContent = sanitizeInAppNotificationContent(pushBody);

  return {
    smsMessage,
    pushTitle,
    pushBody,
    inAppTitle: pushTitle,
    inAppContent,
    icon,
  };
}
